import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Button,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  FormHelperText,
  Modal,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ImageIcon from "@mui/icons-material/Image";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import CloseIcon from "@mui/icons-material/Close";
import { useGetList, useCreate, useGetIdentity } from "react-admin";
import { useNavigate } from "react-router-dom";
import { useClub } from "../club/ClubContext";
import keycloak from "../keycloak";
import { ShopItem, CartEntry } from "./types";

function formatPrice(price: number): string {
  return `€ ${price.toFixed(2).replace(".", ",")}`;
}

// ─── Artikel-Karte für einfache Items (kein Größe, kein Aufdruck) ─────────────

function SimpleArticleCard({
  item,
  lines,
  onLinesChange,
}: {
  item: ShopItem;
  lines: CartEntry[];
  onLinesChange: (lines: CartEntry[]) => void;
}) {
  const qty = lines[0]?.quantity ?? 0;

  const setQty = (next: number) => {
    if (next <= 0) onLinesChange([]);
    else onLinesChange([{ quantity: next }]);
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      <ArticleImage item={item} />
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        <ArticleHeader item={item} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "auto" }}>
          <IconButton size="small" color="primary" disabled={qty === 0} onClick={() => setQty(qty - 1)}>
            <RemoveIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ minWidth: 40, textAlign: "center", fontWeight: qty > 0 ? 700 : 400, color: qty > 0 ? "primary.main" : "text.disabled" }}
          >
            {qty}
          </Typography>
          <IconButton size="small" color="primary" onClick={() => setQty(qty + 1)}>
            <AddIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Artikel-Karte für komplexe Items (Größe und/oder Aufdruck) ───────────────

function VariantLine({
  item,
  line,
  showRemove,
  onChange,
  onRemove,
}: {
  item: ShopItem;
  line: CartEntry;
  showRemove: boolean;
  onChange: (update: Partial<CartEntry>) => void;
  onRemove: () => void;
}) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {item.sizes && item.sizes.length > 0 && (
        <FormControl size="small" fullWidth error={line.quantity > 0 && !line.size}>
          <InputLabel>Größe</InputLabel>
          <Select
            value={line.size ?? ""}
            label="Größe"
            onChange={(e) => onChange({ size: e.target.value as string })}
          >
            {item.sizes.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
          {line.quantity > 0 && !line.size && (
            <FormHelperText>Bitte Größe wählen</FormHelperText>
          )}
        </FormControl>
      )}

      {item.has_imprint && (
        <TextField
          label="Aufdruck (optional)"
          size="small"
          fullWidth
          value={line.imprint ?? ""}
          onChange={(e) => onChange({ imprint: e.target.value })}
          placeholder="z.B. Rückennummer, Name…"
        />
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          size="small"
          color="primary"
          onClick={() => onChange({ quantity: Math.max(1, line.quantity - 1) })}
          disabled={line.quantity <= 1}
        >
          <RemoveIcon />
        </IconButton>
        <Typography
          variant="h6"
          sx={{ minWidth: 36, textAlign: "center", fontWeight: 600, color: "primary.main" }}
        >
          {line.quantity}
        </Typography>
        <IconButton size="small" color="primary" onClick={() => onChange({ quantity: line.quantity + 1 })}>
          <AddIcon />
        </IconButton>
        {showRemove && (
          <IconButton size="small" color="error" onClick={onRemove} sx={{ ml: "auto" }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function ComplexArticleCard({
  item,
  lines,
  onLinesChange,
}: {
  item: ShopItem;
  lines: CartEntry[];
  onLinesChange: (lines: CartEntry[]) => void;
}) {
  const addLine = () => onLinesChange([...lines, { quantity: 1 }]);

  const updateLine = (index: number, update: Partial<CartEntry>) => {
    onLinesChange(lines.map((l, i) => (i === index ? { ...l, ...update } : l)));
  };

  const removeLine = (index: number) => {
    onLinesChange(lines.filter((_, i) => i !== index));
  };

  return (
    <Card sx={{ display: "flex", flexDirection: "column" }}>
      <ArticleImage item={item} />
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <ArticleHeader item={item} />

        {lines.length === 0 ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addLine}
            sx={{ mt: "auto" }}
          >
            Hinzufügen
          </Button>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {lines.map((line, i) => (
                <VariantLine
                  key={i}
                  item={item}
                  line={line}
                  showRemove={true}
                  onChange={(update) => updateLine(i, update)}
                  onRemove={() => removeLine(i)}
                />
              ))}
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addLine}
              sx={{ alignSelf: "flex-start" }}
            >
              Weitere Variante
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Gemeinsame Sub-Komponenten ───────────────────────────────────────────────

function Lightbox({ images, initialIndex, onClose }: { images: string[]; initialIndex: number; onClose: () => void }) {
  const [index, setIndex] = useState(initialIndex);
  const touchStartX = useRef<number | null>(null);

  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prev, next, onClose]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) dx < 0 ? next() : prev();
    touchStartX.current = null;
  };

  return (
    <Modal open onClose={onClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{ outline: "none", position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: -44, right: 0, color: "white", bgcolor: "rgba(0,0,0,0.4)", "&:hover": { bgcolor: "rgba(0,0,0,0.6)" } }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          component="img"
          src={images[index]}
          alt={`Bild ${index + 1}`}
          sx={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", display: "block", borderRadius: 1 }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              onClick={prev}
              sx={{
                position: "absolute",
                left: { xs: 4, sm: -52 },
                top: "50%",
                transform: "translateY(-50%)",
                color: "white",
                bgcolor: "rgba(0,0,0,0.4)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={next}
              sx={{
                position: "absolute",
                right: { xs: 4, sm: -52 },
                top: "50%",
                transform: "translateY(-50%)",
                color: "white",
                bgcolor: "rgba(0,0,0,0.4)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
            <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
              {images.map((_, i) => (
                <Box
                  key={i}
                  onClick={() => setIndex(i)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: i === index ? "white" : "rgba(255,255,255,0.4)",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

function ArticleImage({ item }: { item: ShopItem }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const images =
    item.image_urls && item.image_urls.length > 0
      ? item.image_urls
      : item.image_url
      ? [item.image_url]
      : [];

  if (images.length === 0) {
    return (
      <Box
        sx={{
          height: 180,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "action.hover",
        }}
      >
        <ImageIcon sx={{ fontSize: 64, color: "text.disabled" }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{ position: "relative", cursor: images.length > 1 ? "zoom-in" : "default" }}
        onClick={() => images.length > 1 && setLightboxIndex(0)}
      >
        <CardMedia
          component="img"
          image={images[0]}
          alt={item.name}
          sx={{ height: 180, objectFit: "contain", bgcolor: "background.default", p: 1 }}
        />
        {images.length > 1 && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.45)",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ZoomInIcon sx={{ color: "white", fontSize: 20 }} />
          </Box>
        )}
      </Box>
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

function ArticleHeader({ item }: { item: ShopItem }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {item.name}
      </Typography>
      {item.description && (
        <Typography variant="body2" color="text.secondary">
          {item.description}
        </Typography>
      )}
      <Typography variant="h6" color="primary" mt={0.5}>
        {formatPrice(item.price)}
      </Typography>
    </Box>
  );
}

// ─── Haupt-Seite ─────────────────────────────────────────────────────────────

export default function ShopPage() {
  const { clubId } = useClub();
  const navigate = useNavigate();

  // lines pro Artikel: Record<itemId, CartEntry[]>
  const [entries, setEntries] = useState<Record<number, CartEntry[]>>({});
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "paypal">("cash");
  const [error, setError] = useState<string | null>(null);

  const { data: identity } = useGetIdentity();
  const identityApplied = React.useRef(false);
  React.useEffect(() => {
    if (identityApplied.current) return;
    const id = identity as any;
    const first = id?.first_name ?? id?.firstName ?? keycloak.tokenParsed?.given_name ?? "";
    const last = id?.last_name ?? id?.lastName ?? keycloak.tokenParsed?.family_name ?? "";
    const joined = [first, last].filter(Boolean).join(" ");
    const full = joined || (id?.fullName ?? id?.full_name ?? "");
    const email = id?.email ?? keycloak.tokenParsed?.email ?? "";
    if (full || email) {
      identityApplied.current = true;
      if (full) setCustomerName(full);
      if (email) setCustomerEmail(email);
    }
  }, [identity]);

  const [create, { isPending }] = useCreate();

  const { data: items = [], isPending: itemsLoading } = useGetList<ShopItem>("shop-items", {
    filter: { active: true },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  // Alle Zeilen mit qty > 0 über alle Artikel
  const allOrderLines = useMemo(() => {
    return items.flatMap((item) =>
      (entries[item.id] ?? [])
        .filter((l) => l.quantity > 0)
        .map((l) => ({ item, line: l }))
    );
  }, [items, entries]);

  const total = useMemo(
    () => allOrderLines.reduce((sum, { item, line }) => sum + item.price * line.quantity, 0),
    [allOrderLines]
  );

  function setItemLines(itemId: number, lines: CartEntry[]) {
    setEntries((prev) => {
      if (lines.length === 0) {
        const { [itemId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [itemId]: lines };
    });
  }

  async function handleSubmit() {
    setError(null);

    if (allOrderLines.length === 0) {
      setError("Bitte wähle mindestens einen Artikel aus.");
      return;
    }
    if (!customerName.trim()) {
      setError("Bitte gib deinen Namen ein.");
      return;
    }
    for (const { item, line } of allOrderLines) {
      if (item.sizes?.length && !line.size) {
        setError(`Bitte wähle eine Größe für „${item.name}".`);
        return;
      }
    }

    create(
      "shop-orders",
      {
        data: {
          club_id: clubId,
          customer_name: customerName.trim(),
          customer_email: customerEmail.trim() || undefined,
          notes: notes.trim() || undefined,
          payment_method: paymentMethod,
          items: allOrderLines.map(({ item, line }) => ({
            shop_item_id: item.id,
            quantity: line.quantity,
            size: line.size ?? undefined,
            imprint: line.imprint ?? undefined,
          })),
        },
      },
      {
        onSuccess: (data: any) => {
          // Token in localStorage speichern für "Meine Bestellungen"
          try {
            const key = "dartmin_my_orders";
            const existing = JSON.parse(localStorage.getItem(key) ?? "[]");
            const entry = { token: data.token, id: data.id, created_at: data.created_at, total: data.total };
            localStorage.setItem(key, JSON.stringify([entry, ...existing].slice(0, 50)));
          } catch {}
          navigate(`/shop/order/${data.token}`);
        },
        onError: (err: any) => setError(err?.message ?? "Fehler beim Absenden der Bestellung."),
      }
    );
  }

  if (itemsLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="text.secondary">Derzeit sind keine Artikel verfügbar.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>
        Shop
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 2,
        }}
      >
        {items.map((item) => {
          const isComplex = (item.sizes?.length ?? 0) > 0 || item.has_imprint;
          const lines = entries[item.id] ?? [];
          return isComplex ? (
            <ComplexArticleCard
              key={item.id}
              item={item}
              lines={lines}
              onLinesChange={(l) => setItemLines(item.id, l)}
            />
          ) : (
            <SimpleArticleCard
              key={item.id}
              item={item}
              lines={lines}
              onLinesChange={(l) => setItemLines(item.id, l)}
            />
          );
        })}
      </Box>

      <Box
        mt={4}
        p={{ xs: 2, sm: 3 }}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
      >
        <Typography variant="h6" fontWeight={600} mb={2} display="flex" alignItems="center" gap={1}>
          <ShoppingCartIcon /> Deine Bestellung
        </Typography>

        {allOrderLines.length === 0 ? (
          <Typography color="text.secondary" mb={2}>
            Noch keine Artikel ausgewählt.
          </Typography>
        ) : (
          <Box mb={2}>
            {allOrderLines.map(({ item, line }, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  py: 0.75,
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {item.name} × {line.quantity}
                  </Typography>
                  {line.size && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Größe: {line.size}
                    </Typography>
                  )}
                  {line.imprint && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Aufdruck: {line.imprint}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" fontWeight={500} whiteSpace="nowrap">
                  {formatPrice(item.price * line.quantity)}
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 1 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" fontWeight={700}>Gesamt</Typography>
              <Typography variant="subtitle1" fontWeight={700} color="primary">
                {formatPrice(total)}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ px: 0.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
              Bestellt von
            </Typography>
            <Typography fontWeight={500}>{customerName || "–"}</Typography>
          </Box>
          <TextField
            label="Anmerkung (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
          />

          <FormControl>
            <Typography variant="caption" color="text.secondary" mb={0.25}>
              Zahlungsart
            </Typography>
            <RadioGroup
              row
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as "cash" | "paypal")}
            >
              <FormControlLabel value="cash" control={<Radio />} label="Bar" />
              <FormControlLabel value="paypal" control={<Radio />} label="PayPal" />
            </RadioGroup>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={{ mt: 3 }}
          onClick={handleSubmit}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={18} color="inherit" /> : <ShoppingCartIcon />}
        >
          {isPending ? "Wird gesendet…" : "Jetzt bestellen"}
        </Button>
      </Box>
    </Box>
  );
}
